import { CrossTabMessage } from './cross-tab-message';

// ===========================================================================
// 同じブラウザの別タブへの通知（アプリ全体でここだけ）
// ===========================================================================
// 以前は localStorage の storage イベント（site-provider.js の
// site_config_reload / last_config_change_timestamp）と、デザイン編集の
// BroadcastChannel が別々にあった。**運び方はここ 1 本**にする。
//
// ---- なぜ BroadcastChannel か（storage イベントではなく）----
//
//   storage イベント … 送るたびに localStorage へ**同期で書き込む**
//                      （メインスレッドを止め、ディスクに永続化する）。
//                      さらに**どのキーの書き込みでも**全タブの全リスナーが起きる。
//                      値が変わらないと発火しないので、毎回時刻を混ぜて書く必要があり、
//                      通知が終わっても値が localStorage に残り続ける。
//   BroadcastChannel … 購読しているタブにだけ、メモリ上の構造化複製で届く。
//                      書き込みも残り値も無い。
//
//   BroadcastChannel は Safari 15.4 以降。package.json の browserslist
//   （>0.2%, not dead）には、まだ非対応の iOS Safari 12.2〜14.8・Safari 14.1・
//   Android 4.4 が含まれる（手元の caniuse-lite 1.0.30001488 = 2023-05 版で確認。
//   更新すると利用率 0.2% 未満で外れる版もある）。
//   無い環境では open() が null を返し、通知が届かないだけで画面は止めない
//   （呼び出し側はフォーカス復帰などの取り直しを持っている）。
//   = 古い Safari では別タブの保存・設定変更がすぐには伝わらない。
//
// ⚠ 「localStorage に置いた値の同期」（use-local-storage.js）はここの担当ではない。
//   あれは値そのものが localStorage に住んでいて、storage イベントが正しい道具。
//
// ---- 形 ----
//
//   チャンネルはタブごとに 1 本（シングルトン）。購読は topic ごとに束ねる。
//   購読が 0 になったら閉じる（開きっぱなしにしない）。
//   BroadcastChannel は自分が送ったものを自分には返さないので、
//   同じタブの別の購読者にも届かない（自分の操作は自分で後始末する前提）。
//   送るもの・受けるものは CrossTabMessage のサブクラス（このディレクトリ）だけ。

export const CROSS_TAB_CHANNEL_NAME = 'cms-react-admin';

export class CrossTabChannel {
    constructor(name) {
        this.name = name;
        this.channel = null;
        /** @type {Map<string, Set<Function>>} topic → handlers */
        this.handlers = new Map();
    }

    open() {
        if (this.channel) return this.channel;
        if (typeof BroadcastChannel === 'undefined') return null;
        try {
            this.channel = new BroadcastChannel(this.name);
        } catch (e) {
            console.warn('[cross-tab] BroadcastChannel unavailable:', e);
            return null;
        }
        this.channel.onmessage = (ev) => this.dispatch(ev?.data);
        return this.channel;
    }

    closeIfIdle() {
        if (this.handlers.size > 0 || !this.channel) return;
        this.channel.close();
        this.channel = null;
    }

    dispatch(data) {
        const message = CrossTabMessage.parse(data);
        if (!message) return;
        (this.handlers.get(message.topic) || []).forEach((handler) => {
            // 1 つの受け口の失敗で、同じ topic の他の受け口を止めない。
            try {
                handler(message);
            } catch (e) {
                console.error(`[cross-tab] handler for ${message.topic} failed:`, e);
            }
        });
    }

    /**
     * @param {typeof CrossTabMessage} MessageClass 受けたいメッセージのクラス
     * @param {(message: CrossTabMessage) => void} handler
     * @returns {() => void} 購読解除
     */
    subscribe(MessageClass, handler) {
        const { topic } = MessageClass;
        if (!topic) throw new Error('CrossTabChannel: topic の無いクラスは購読できません');
        if (!this.handlers.has(topic)) this.handlers.set(topic, new Set());
        this.handlers.get(topic).add(handler);
        this.open();
        return () => {
            const set = this.handlers.get(topic);
            if (!set) return;
            set.delete(handler);
            if (set.size === 0) this.handlers.delete(topic);
            this.closeIfIdle();
        };
    }

    /**
     * 別タブへ送る。届けられなかった（チャンネルが無い / 失敗）ら false。
     * @param {CrossTabMessage} message
     */
    post(message) {
        if (!(message instanceof CrossTabMessage)) {
            throw new TypeError('CrossTabChannel.post: CrossTabMessage のインスタンスを渡してください');
        }
        const channel = this.open();
        if (!channel) return false;
        try {
            channel.postMessage(message.toData());
            return true;
        } catch (e) {
            console.warn(`[cross-tab] post ${message.topic} failed:`, e);
            return false;
        } finally {
            // 購読していないタブが送るだけのときは開きっぱなしにしない。
            // 送った分は postMessage の時点で相手の列に積まれているので、閉じても届く。
            this.closeIfIdle();
        }
    }
}

/** アプリ全体で 1 本のチャンネル */
export const crossTabChannel = new CrossTabChannel(CROSS_TAB_CHANNEL_NAME);
